import { useState, useEffect, useCallback, useRef, Fragment } from 'react';
import { financeApi, storeApi } from '../lib/api';
import { CalendarDays, ChevronDown, ChevronRight, SlidersHorizontal } from 'lucide-react';
import { EmptyState } from '../components/EmptyState';
import { FinanceResultModeToggle } from '../components/FinanceResultModeToggle';
import { financeChannelLabels, type FinanceChannel } from '../lib/finance-channel-api';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface DailyData {
  date: string;
  ordersCount: number;
  revenueGross: number;
  revenueNet: number;
  refundsGross?: number;
  revenueGrossPreRefund?: number;
  shippingGrossPreRefund?: number;
  vatSales: number;
  shippingGross: number;
  shippingVat: number;
  costProducts: number;
  costShipping: number;
  costFees: number;
  costFixed: number;
  costReturns?: number;
  adSpend: number;
  ctr: number;
  cpc: number;
  roas: number;
  grossProfit: number;
  netProfit: number;
  _frozen?: boolean;
}

interface CashflowTotals {
  ordersCount: number;
  revenueGross: number;
  revenueNet: number;
  refundsGross?: number;
  revenueGrossPreRefund?: number;
  shippingGrossPreRefund?: number;
  vatSales: number;
  shippingGross: number;
  shippingVat: number;
  costProducts: number;
  costShipping: number;
  costFees: number;
  costFixed: number;
  costReturns?: number;
  adSpend: number;
  ctr: number;
  cpc: number;
  roas: number;
  grossProfit: number;
  netProfit: number;
  revenue: number;
}

interface TaxBreakdown {
  vatToPay: number;
  incomeTax: number;
  healthIns: number;
  socialIns: number;
  totalTaxes: number;
  adVatWnt: number;
}

interface CashflowResponse {
  daily: DailyData[];
  totals: CashflowTotals;
  taxBreakdown: TaxBreakdown;
}

interface FinanceConfig {
  vatEnabled?: boolean;
  [key: string]: unknown;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const POLISH_DAYS_SHORT = ['Nd', 'Pon', 'Wt', 'Śr', 'Czw', 'Pt', 'Sob'];

function formatPLN(value: number): string {
  return value.toLocaleString('pl-PL', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function formatDateOnly(iso: string): string {
  const d = new Date(iso);
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yyyy = d.getFullYear();
  return `${dd}.${mm}.${yyyy}`;
}

function formatDayName(iso: string): string {
  const d = new Date(iso);
  return POLISH_DAYS_SHORT[d.getDay()];
}

/** Return current date as YYYY-MM-DD in Warsaw timezone (not UTC!) */
function warsawToday(): string {
  return new Intl.DateTimeFormat('sv-SE', { timeZone: 'Europe/Warsaw' }).format(new Date());
}

/** Format Date as YYYY-MM-DD using local (non-UTC) components — avoids timezone shift from toISOString() */
function toLocalDateStr(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function todayISO(): string {
  return warsawToday();
}

function firstDayOfMonthISO(): string {
  const today = warsawToday();
  return `${today.slice(0, 4)}-${today.slice(5, 7)}-01`;
}

// Quick date filter presets
function getDatePreset(preset: string): { from: string; to: string } {
  const to = warsawToday();

  switch (preset) {
    case 'today':
      return { from: to, to };
    case 'yesterday': {
      const y = new Date(to + 'T12:00:00');
      y.setDate(y.getDate() - 1);
      const yStr = toLocalDateStr(y);
      return { from: yStr, to: yStr };
    }
    case '7d': {
      const d = new Date(to + 'T12:00:00');
      d.setDate(d.getDate() - 6);
      return { from: toLocalDateStr(d), to };
    }
    case '14d': {
      const d = new Date(to + 'T12:00:00');
      d.setDate(d.getDate() - 13);
      return { from: toLocalDateStr(d), to };
    }
    case 'this_month':
      return { from: `${to.slice(0, 4)}-${to.slice(5, 7)}-01`, to };
    case 'last_month': {
      const now = new Date(to + 'T12:00:00');
      const first = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const last = new Date(now.getFullYear(), now.getMonth(), 0);
      return { from: toLocalDateStr(first), to: toLocalDateStr(last) };
    }
    case 'max':
      return { from: '2020-01-01', to };
    default:
      return { from: firstDayOfMonthISO(), to };
  }
}

const DATE_PRESETS = [
  { key: 'today', label: 'Dzisiaj' },
  { key: 'yesterday', label: 'Wczoraj' },
  { key: '7d', label: '7 dni' },
  { key: '14d', label: '14 dni' },
  { key: 'this_month', label: 'Ten miesiąc' },
  { key: 'last_month', label: 'Ost. miesiąc' },
  { key: 'max', label: 'Maksymalnie' },
];

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function FinanceCashflowPage() {
  // Filters
  const [dateFrom, setDateFrom] = useState(firstDayOfMonthISO);
  const [dateTo, setDateTo] = useState(todayISO);
  const [selectedProductIds, setSelectedProductIds] = useState<string[]>([]);
  const [completedOnly, setCompletedOnly] = useState(false);
  const [activePreset, setActivePreset] = useState<string>('this_month');

  // Data
  const [data, setData] = useState<CashflowResponse | null>(null);
  const [vatEnabled, setVatEnabled] = useState(false);
  const [snapshotEnabled, setSnapshotEnabled] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Store filter
  const [stores, setStores] = useState<{ id: string; name: string }[]>([]);
  const [selectedStoreId, setSelectedStoreId] = useState<string>('all');

  // Daily channel breakdown: expanded day + lazy per-day results (full table columns per channel)
  const [expandedDate, setExpandedDate] = useState<string | null>(null);
  const [channelDays, setChannelDays] = useState<Record<string, { status: 'loading' | 'error' | 'ok'; rows: { channel: string; day: DailyData | null }[] }>>({});

  // Tax deduction settings (which fields to show)
  const [taxSettings, setTaxSettings] = useState<{ includeZusTaxes: boolean; incomeTax: boolean; healthIns: boolean; socialIns: boolean }>({
    includeZusTaxes: false, incomeTax: false, healthIns: false, socialIns: false,
  });

  // Products for multi-select
  const [allProducts, setAllProducts] = useState<{ externalProductId: string; productName: string; sku?: string; storeId?: string }[]>([]);
  const [productDropdownOpen, setProductDropdownOpen] = useState(false);
  const [productSearch, setProductSearch] = useState('');
  const productDropdownRef = useRef<HTMLDivElement>(null);

  // Shipping method filter
  const [availableShippingMethods, setAvailableShippingMethods] = useState<{ value: string; label: string }[]>([]);
  const [selectedShippingMethods, setSelectedShippingMethods] = useState<string[]>([]);
  const [shippingDropdownOpen, setShippingDropdownOpen] = useState(false);
  const [shippingSearch, setShippingSearch] = useState('');
  const shippingDropdownRef = useRef<HTMLDivElement>(null);

  // Shipment date filter (tracking number creation date)
  const [shipmentDateFrom, setShipmentDateFrom] = useState('');
  const [shipmentDateTo, setShipmentDateTo] = useState('');

  // Advanced filters toggle
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);

  // Sync state (all stores)
  const [syncing, setSyncing] = useState(false);

  // ------------------------------------------------------------------
  // Fetch config + stores on mount
  // ------------------------------------------------------------------
  useEffect(() => {
    financeApi
      .getConfig()
      .then((res) => {
        const cfg = (res.data ?? res) as FinanceConfig;
        setVatEnabled(!!cfg.vatEnabled);
      })
      .catch(() => {
        /* config fetch is best-effort */
      });
    financeApi
      .getSettings()
      .then((res) => {
        const settings = (res.data ?? res) as Record<string, unknown>;
        setSnapshotEnabled(!!settings.cashflowSnapshotEnabled);
        const taxDed = (settings.taxDeductions as Record<string, boolean>) || {};
        setTaxSettings({
          includeZusTaxes: !!settings.includeZusTaxes,
          incomeTax: !!taxDed.incomeTax,
          healthIns: !!taxDed.healthIns,
          socialIns: !!taxDed.socialIns,
        });
      })
      .catch(() => {
        /* settings fetch is best-effort */
      });
    storeApi
      .getAll()
      .then((res) => {
        const list = ((res.data ?? res) as { id: string; name: string }[]) || [];
        setStores(list);
      })
      .catch(() => {
        /* store fetch is best-effort */
      });
    financeApi
      .getCalculatorProducts()
      .then((res) => {
        const list = ((res.data ?? res) as { externalProductId: string; productName: string; sku?: string; storeId?: string }[]) || [];
        setAllProducts(list);
      })
      .catch(() => {
        /* products fetch is best-effort */
      });
  }, []);

  // Fetch shipping methods — reload when selected store changes
  useEffect(() => {
    const storeId = selectedStoreId && selectedStoreId !== 'all' ? selectedStoreId : undefined;
    financeApi
      .getShippingMethods(storeId)
      .then((res) => {
        const list = ((res.data ?? res) as { value: string; label: string }[]) || [];
        setAvailableShippingMethods(list);
        // Clear selection if previously selected methods are no longer available
        setSelectedShippingMethods(prev => {
          const available = new Set(list.map(m => m.value));
          const filtered = prev.filter(v => available.has(v));
          return filtered.length !== prev.length ? filtered : prev;
        });
      })
      .catch(() => {
        /* shipping methods fetch is best-effort */
      });
  }, [selectedStoreId]);

  // Close product dropdown on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (productDropdownRef.current && !productDropdownRef.current.contains(e.target as Node)) {
        setProductDropdownOpen(false);
        setProductSearch('');
      }
      if (shippingDropdownRef.current && !shippingDropdownRef.current.contains(e.target as Node)) {
        setShippingDropdownOpen(false);
        setShippingSearch('');
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleSyncAll = async () => {
    if (syncing || stores.length === 0) return;
    setSyncing(true);
    try {
      // Sync WooCommerce stores + Facebook ads in parallel
      await Promise.all([
        (async () => { for (const store of stores) { await storeApi.sync(store.id); } })(),
        financeApi.syncFacebookAds().catch(() => {}), // FB sync — ignore errors (rate limit etc.)
      ]);
      await fetchData();
    } catch {
      // silent
    } finally {
      setSyncing(false);
    }
  };

  // ------------------------------------------------------------------
  // Fetch cashflow data
  // ------------------------------------------------------------------
  const fetchData = useCallback(async (overrideFrom?: string, overrideTo?: string, overrideCompleted?: boolean, overrideStoreId?: string) => {
    setLoading(true);
    setError(null);
    // Filters changed — drop cached channel breakdowns and collapse the expanded day
    setChannelDays({});
    setExpandedDate(null);
    try {
      const params: Record<string, unknown> = {
        dateFrom: overrideFrom ?? dateFrom,
        dateTo: overrideTo ?? dateTo,
        completedOnly: overrideCompleted ?? completedOnly,
      };
      const sid = overrideStoreId ?? selectedStoreId;
      if (sid && sid !== 'all') {
        params.storeIds = sid;
      }
      if (selectedProductIds.length > 0) {
        params.productIds = selectedProductIds.join(',');
      }
      if (selectedShippingMethods.length > 0) {
        params.shippingMethods = selectedShippingMethods.join(',');
      }
      if (shipmentDateFrom) {
        params.shipmentDateFrom = shipmentDateFrom;
      }
      if (shipmentDateTo) {
        params.shipmentDateTo = shipmentDateTo;
      }
      const res = await financeApi.getCashflow(params as Parameters<typeof financeApi.getCashflow>[0]);
      setData((res.data ?? res) as CashflowResponse);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Wystąpił błąd podczas pobierania danych';
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo, selectedProductIds, completedOnly, selectedStoreId, selectedShippingMethods, shipmentDateFrom, shipmentDateTo]);

  // Auto-fetch on mount
  useEffect(() => {
    fetchData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Lazy per-day channel breakdown (full table columns, computed by the same cashflow engine)
  const loadChannelDay = useCallback(async (date: string) => {
    setChannelDays((prev) => ({ ...prev, [date]: { status: 'loading', rows: [] } }));
    try {
      const params: Record<string, unknown> = { date, completedOnly };
      if (selectedStoreId !== 'all') params.storeIds = selectedStoreId;
      if (selectedShippingMethods.length > 0) params.shippingMethods = selectedShippingMethods.join(',');
      if (shipmentDateFrom) params.shipmentDateFrom = shipmentDateFrom;
      if (shipmentDateTo) params.shipmentDateTo = shipmentDateTo;
      const res = await financeApi.getCashflowChannelDay(params as Parameters<typeof financeApi.getCashflowChannelDay>[0]);
      const payload = (res.data ?? res) as { channels?: { channel: string; day: DailyData | null }[] };
      setChannelDays((prev) => ({ ...prev, [date]: { status: 'ok', rows: payload.channels ?? [] } }));
    } catch {
      setChannelDays((prev) => ({ ...prev, [date]: { status: 'error', rows: [] } }));
    }
  }, [completedOnly, selectedStoreId, selectedShippingMethods, shipmentDateFrom, shipmentDateTo]);

  const toggleChannelDay = (date: string) => {
    if (expandedDate === date) {
      setExpandedDate(null);
      return;
    }
    setExpandedDate(date);
    if (!channelDays[date]) void loadChannelDay(date);
  };

  // ------------------------------------------------------------------
  // Render
  // ------------------------------------------------------------------

  const daily = data?.daily ?? [];
  const totals = data?.totals;
  const tax = data?.taxBreakdown;

  // Total number of table columns (used by the expandable channel row colSpan)
  const tableColumnCount = 17 + (vatEnabled ? 1 : 0);

  return (
    <div className="space-y-6">
      {/* ============================================================= */}
      {/* Header                                                        */}
      {/* ============================================================= */}
      <div className="animate-fade-in">
        <h1 className="text-3xl font-bold text-text-primary">
          <span className="text-gradient">Cashflow</span>
        </h1>
        <p className="text-text-secondary mt-1">Dzienny rozkład finansowy</p>
      </div>

      {/* ============================================================= */}
      {/* Filters bar                                                   */}
      {/* ============================================================= */}
      <div className="space-y-3 p-4 rounded-xl bg-surface-raised/50 border border-border-subtle">
        {/* Quick date presets + Store filter badges — single row, stores wrap as a group */}
        <div className="flex flex-wrap items-center gap-x-2 gap-y-2">
          {/* Date presets group */}
          <div className="flex items-center gap-2">
            <span className="text-sm leading-none" title="Filtry datowe">📅</span>
            {DATE_PRESETS.map((preset) => (
              <button
                key={preset.key}
                onClick={async () => {
                  if (preset.key === 'max') {
                    // Smart Maksymalnie: detect earliest ad data
                    setActivePreset(preset.key);
                    const to = todayISO();
                    setDateTo(to);
                    try {
                      const res = await financeApi.getAdDateRange(
                        selectedProductIds.length > 0 ? { productIds: selectedProductIds.join(',') } : undefined
                      );
                      const adFrom = (res.data as { from: string | null })?.from;
                      const from = adFrom || '2020-01-01';
                      setDateFrom(from);
                      fetchData(from, to);
                    } catch {
                      const from = '2020-01-01';
                      setDateFrom(from);
                      fetchData(from, to);
                    }
                    return;
                  }
                  const { from, to } = getDatePreset(preset.key);
                  setDateFrom(from);
                  setDateTo(to);
                  setActivePreset(preset.key);
                  fetchData(from, to);
                }}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                  activePreset === preset.key
                    ? 'bg-accent-primary/20 text-accent-primary border border-accent-primary/30'
                    : 'bg-surface-hover/50 text-text-secondary border border-border-subtle hover:text-text-primary hover:border-border'
                }`}
              >
                {preset.label}
              </button>
            ))}
          </div>

          {/* Store badges group — wraps to second line as a whole unit */}
          {stores.length > 0 && (
            <div className="flex items-center gap-2 lg:ml-[30px]">
              <span className="text-sm leading-none" title="Filtry sklepowe">🏪</span>
              <button
                onClick={() => {
                  setSelectedStoreId('all');
                  setSelectedProductIds([]);
                  fetchData(undefined, undefined, undefined, 'all');
                }}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                  selectedStoreId === 'all'
                    ? 'bg-purple-500/20 text-purple-300 border border-purple-500/30'
                    : 'bg-surface-hover/50 text-text-secondary border border-border-subtle hover:text-text-primary hover:border-border'
                }`}
              >
                Wszystkie sklepy
              </button>
              {stores.map((s) => (
                <button
                  key={s.id}
                  onClick={() => {
                    setSelectedStoreId(s.id);
                    setSelectedProductIds([]);
                    fetchData(undefined, undefined, undefined, s.id);
                  }}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                    selectedStoreId === s.id
                      ? 'bg-purple-500/20 text-purple-300 border border-purple-500/30'
                      : 'bg-surface-hover/50 text-text-secondary border border-border-subtle hover:text-text-primary hover:border-border'
                  }`}
                >
                  {s.name}
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="flex flex-wrap items-end gap-4">
          {/* Date from */}
          <div className="flex flex-col gap-1">
            <label className="text-xs text-text-secondary">Od</label>
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => { setDateFrom(e.target.value); setActivePreset(''); }}
              className="px-3 py-2 rounded-lg bg-surface-base border border-border text-text-primary text-sm focus:outline-none focus:border-accent-primary/50 transition-colors"
            />
          </div>

          {/* Date to */}
          <div className="flex flex-col gap-1">
            <label className="text-xs text-text-secondary">Do</label>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => { setDateTo(e.target.value); setActivePreset(''); }}
              className="px-3 py-2 rounded-lg bg-surface-base border border-border text-text-primary text-sm focus:outline-none focus:border-accent-primary/50 transition-colors"
            />
          </div>

          {/* Products multi-select */}
          <div className="flex flex-col gap-1 flex-1 min-w-[200px] relative" ref={productDropdownRef}>
            <label className="text-xs text-text-secondary">Produkty</label>
            <div className="flex items-center gap-1.5">
              <button
                type="button"
                onClick={() => setProductDropdownOpen(!productDropdownOpen)}
                className="flex-1 px-3 py-2 rounded-lg bg-surface-base border border-border text-sm text-left focus:outline-none focus:border-accent-primary/50 transition-colors flex items-center justify-between gap-2"
              >
                <span className={selectedProductIds.length > 0 ? 'text-text-primary' : 'text-text-disabled'}>
                  {selectedProductIds.length > 0
                    ? `Wybrano ${selectedProductIds.length} produktów`
                    : 'Wszystkie produkty'}
                </span>
                <svg className={`w-4 h-4 text-text-muted transition-transform ${productDropdownOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {selectedProductIds.length > 0 && (
                <button
                  type="button"
                  onClick={() => setSelectedProductIds([])}
                  className="px-2 py-2 rounded-lg bg-danger/10 border border-danger/20 text-xs text-danger hover:bg-danger/20 transition-colors whitespace-nowrap"
                >
                  Wyczyść
                </button>
              )}
            </div>
            {productDropdownOpen && (
              <div className="absolute top-full left-0 right-0 mt-1 z-50 bg-surface-raised border border-border rounded-lg shadow-xl max-h-72 flex flex-col">
                <div className="sticky top-0 bg-surface-raised p-2 border-b border-border-subtle">
                  <input
                    type="text"
                    value={productSearch}
                    onChange={(e) => setProductSearch(e.target.value)}
                    placeholder="Szukaj produktu..."
                    className="w-full px-2.5 py-1.5 rounded-md bg-surface-base border border-border text-sm text-text-primary placeholder-text-muted focus:outline-none focus:border-accent-primary/50"
                    autoFocus
                  />
                </div>
                <div className="overflow-y-auto flex-1">
                  {selectedProductIds.length > 0 && (
                    <button
                      type="button"
                      onClick={() => setSelectedProductIds([])}
                      className="w-full px-3 py-2 text-xs text-danger hover:bg-danger/10 text-left border-b border-border-subtle"
                    >
                      Wyczyść wybór
                    </button>
                  )}
                  {allProducts
                    .filter((p) => {
                      // Filter by selected store
                      if (selectedStoreId && selectedStoreId !== 'all' && p.storeId && p.storeId !== selectedStoreId) return false;
                      if (!productSearch.trim()) return true;
                      const q = productSearch.toLowerCase();
                      return p.productName.toLowerCase().includes(q) || (p.sku && p.sku.toLowerCase().includes(q));
                    })
                    .map((p) => (
                    <label
                      key={p.externalProductId}
                      className={`flex items-center gap-2 px-3 py-2 cursor-pointer transition-colors ${
                        selectedProductIds.includes(p.externalProductId)
                          ? 'bg-accent-primary/5 text-text-primary'
                          : 'text-text-secondary hover:bg-surface-hover/40'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={selectedProductIds.includes(p.externalProductId)}
                        onChange={() => {
                          setSelectedProductIds(prev =>
                            prev.includes(p.externalProductId)
                              ? prev.filter(id => id !== p.externalProductId)
                              : [...prev, p.externalProductId]
                          );
                        }}
                        className="w-3.5 h-3.5 rounded border-border bg-surface-hover text-accent-primary focus:ring-accent-primary/30"
                      />
                      <span className="text-sm truncate flex-1">{p.productName}</span>
                      {p.sku && <span className="text-[10px] text-text-muted">{p.sku}</span>}
                    </label>
                  ))}
                  {allProducts.length === 0 && (
                    <div className="px-3 py-4 text-sm text-text-muted text-center">Brak produktów</div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Advanced filters toggle — height matched via identical padding to date inputs */}
          <button
            type="button"
            onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
            className={`self-end h-[38px] flex items-center gap-1.5 px-3 rounded-lg text-sm font-medium transition-colors ${
              showAdvancedFilters || selectedShippingMethods.length > 0 || shipmentDateFrom || shipmentDateTo
                ? 'bg-accent-primary/15 text-accent-primary border border-accent-primary/30'
                : 'bg-surface-hover/50 text-text-secondary border border-border-subtle hover:text-text-primary hover:border-border'
            }`}
            title="Zaawansowane filtry"
          >
            <SlidersHorizontal className="w-3.5 h-3.5" />
            {(selectedShippingMethods.length > 0 || shipmentDateFrom || shipmentDateTo) && (
              <span className="w-1.5 h-1.5 rounded-full bg-accent-primary" />
            )}
          </button>

          <FinanceResultModeToggle
            completedOnly={completedOnly}
            onChange={(next) => {
              setCompletedOnly(next);
              fetchData(undefined, undefined, next);
            }}
          />

          {/* Freeze range button — only when snapshot is enabled in settings */}
          {snapshotEnabled && (
            <button
              onClick={async () => {
                if (!dateFrom || !dateTo) return;
                try {
                  await financeApi.freezeCashflowRange(dateFrom, dateTo);
                  fetchData();
                } catch {
                  // silent
                }
              }}
              disabled={loading || !data}
              title="Zamroź dane za wybrany zakres"
              className="px-3 py-2 rounded-lg bg-info/10 text-accent-info text-sm border border-info/20 hover:bg-info/20 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
            >
              &#10052; Zamroź
            </button>
          )}

          {/* Sync button (same as Financial Panel) */}
          <button
            onClick={handleSyncAll}
            disabled={syncing || stores.length === 0}
            className="px-3 py-2 rounded-lg bg-success/10 text-success text-sm border border-success/20 hover:bg-success/20 transition-colors disabled:opacity-30 disabled:cursor-not-allowed flex items-center gap-1.5"
          >
            <svg className={`w-3.5 h-3.5 ${syncing ? 'animate-spin' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            {syncing ? 'Synchronizuję...' : 'Synchronizuj'}
          </button>

          {/* Filter button */}
          <button
            onClick={() => fetchData()}
            disabled={loading}
            className="px-5 py-2 rounded-lg bg-accent-primary/20 text-accent-primary font-semibold text-sm border border-accent-primary/30 hover:bg-accent-primary/30 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Wczytywanie...' : 'Filtruj'}
          </button>
        </div>

        {/* Advanced filters (collapsible) */}
        {showAdvancedFilters && (
          <div className="flex flex-wrap items-end gap-4 pt-3 border-t border-border-subtle animate-fade-in">
            {/* Shipping method multi-select */}
            <div className="flex flex-col gap-1 flex-1 min-w-[200px] relative" ref={shippingDropdownRef}>
              <label className="text-xs text-text-secondary">Forma dostawy</label>
              <div className="flex items-center gap-1.5">
                <button
                  type="button"
                  onClick={() => setShippingDropdownOpen(!shippingDropdownOpen)}
                  className="flex-1 px-3 py-2 rounded-lg bg-surface-base border border-border text-sm text-left focus:outline-none focus:border-accent-primary/50 transition-colors flex items-center justify-between gap-2"
                >
                  <span className={selectedShippingMethods.length > 0 ? 'text-text-primary' : 'text-text-disabled'}>
                    {selectedShippingMethods.length > 0
                      ? `Wybrano ${selectedShippingMethods.length}`
                      : 'Wszystkie metody'}
                  </span>
                  <svg className={`w-4 h-4 text-text-muted transition-transform ${shippingDropdownOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                {selectedShippingMethods.length > 0 && (
                  <button
                    type="button"
                    onClick={() => setSelectedShippingMethods([])}
                    className="px-2 py-2 rounded-lg bg-danger/10 border border-danger/20 text-xs text-danger hover:bg-danger/20 transition-colors whitespace-nowrap"
                  >
                    Wyczyść
                  </button>
                )}
              </div>
              {shippingDropdownOpen && (
                <div className="absolute top-full left-0 right-0 mt-1 z-50 bg-surface-raised border border-border rounded-lg shadow-xl max-h-72 flex flex-col">
                  <div className="sticky top-0 bg-surface-raised p-2 border-b border-border-subtle">
                    <input
                      type="text"
                      value={shippingSearch}
                      onChange={(e) => setShippingSearch(e.target.value)}
                      placeholder="Szukaj metody..."
                      className="w-full px-2.5 py-1.5 rounded-md bg-surface-base border border-border text-sm text-text-primary placeholder-text-muted focus:outline-none focus:border-accent-primary/50"
                      autoFocus
                    />
                  </div>
                  <div className="overflow-y-auto flex-1">
                    {selectedShippingMethods.length > 0 && (
                      <button
                        type="button"
                        onClick={() => setSelectedShippingMethods([])}
                        className="w-full px-3 py-2 text-xs text-danger hover:bg-danger/10 text-left border-b border-border-subtle"
                      >
                        Wyczyść wybór
                      </button>
                    )}
                    {availableShippingMethods
                      .filter((m) => {
                        if (!shippingSearch.trim()) return true;
                        return m.label.toLowerCase().includes(shippingSearch.toLowerCase());
                      })
                      .map((m) => (
                      <label
                        key={m.value}
                        className={`flex items-center gap-2 px-3 py-2 cursor-pointer transition-colors ${
                          selectedShippingMethods.includes(m.value)
                            ? 'bg-accent-primary/5 text-text-primary'
                            : 'text-text-secondary hover:bg-surface-hover/40'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={selectedShippingMethods.includes(m.value)}
                          onChange={() => {
                            setSelectedShippingMethods(prev =>
                              prev.includes(m.value)
                                ? prev.filter(v => v !== m.value)
                                : [...prev, m.value]
                            );
                          }}
                          className="w-3.5 h-3.5 rounded border-border bg-surface-hover text-accent-primary focus:ring-accent-primary/30"
                        />
                        <span className="text-sm truncate flex-1">{m.label}</span>
                      </label>
                    ))}
                    {availableShippingMethods.length === 0 && (
                      <div className="px-3 py-4 text-sm text-text-muted text-center">Brak metod wysyłki</div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Shipment date filter (tracking number creation) */}
            <div className="flex flex-col gap-1">
              <label className="text-xs text-text-secondary">Data nadania od</label>
              <input
                type="date"
                value={shipmentDateFrom}
                onChange={(e) => setShipmentDateFrom(e.target.value)}
                className="px-3 py-2 rounded-lg bg-surface-base border border-border text-text-primary text-sm focus:outline-none focus:border-accent-primary/50 transition-colors"
              />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs text-text-secondary">Data nadania do</label>
              <input
                type="date"
                value={shipmentDateTo}
                onChange={(e) => setShipmentDateTo(e.target.value)}
                className="px-3 py-2 rounded-lg bg-surface-base border border-border text-text-primary text-sm focus:outline-none focus:border-accent-primary/50 transition-colors"
              />
            </div>
          </div>
        )}
      </div>

      {/* ============================================================= */}
      {/* Error state                                                   */}
      {/* ============================================================= */}
      {error && (
        <div className="p-4 rounded-xl bg-danger/10 border border-danger/30 text-danger text-sm">
          {error}
        </div>
      )}


      {/* ============================================================= */}
      {/* Loading state                                                 */}
      {/* ============================================================= */}
      {loading && (
        <div className="flex items-center justify-center py-16">
          <div className="w-8 h-8 border-2 border-accent-primary/30 border-t-neon-cyan rounded-full animate-spin" />
        </div>
      )}

      {/* ============================================================= */}
      {/* Cashflow table                                                */}
      {/* ============================================================= */}
      {!loading && data && (
        <div className="rounded-xl border border-border-subtle bg-surface-raised/50 overflow-hidden">
          <div className="overflow-auto max-h-[calc(100vh-5rem)]">
            <table className="w-full text-xs text-left whitespace-nowrap">
              {/* ---- Column group headers ---- */}
              <thead>
                <tr>
                  {/* DATA */}
                  <th className="sticky top-0 z-20 h-8 px-2 py-1.5 text-center text-[11px] font-semibold text-accent-info bg-surface-raised border-b border-border-subtle" colSpan={2}>
                    DATA
                  </th>
                  {/* SPRZEDAZ */}
                  <th
                    className="sticky top-0 z-20 h-8 px-2 py-1.5 text-center text-[11px] font-semibold text-success bg-surface-raised border-b border-border-subtle"
                    colSpan={vatEnabled ? 6 : 5}
                  >
                    SPRZEDAŻ
                  </th>
                  {/* KOSZTY */}
                  <th className="sticky top-0 z-20 h-8 px-2 py-1.5 text-center text-[11px] font-semibold text-danger bg-surface-raised border-b border-border-subtle" colSpan={4}>
                    KOSZTY
                  </th>
                  {/* REKLAMA */}
                  <th className="sticky top-0 z-20 h-8 px-2 py-1.5 text-center text-[11px] font-semibold text-purple-300 bg-surface-raised border-b border-border-subtle" colSpan={4}>
                    REKLAMA
                  </th>
                  {/* WYNIK */}
                  <th className="sticky top-0 z-20 h-8 px-2 py-1.5 text-center text-[11px] font-semibold text-warning bg-surface-raised border-b border-border-subtle" colSpan={2}>
                    WYNIK
                  </th>
                </tr>

                {/* ---- Sub-column headers ---- */}
                <tr className="border-b border-border-subtle text-text-secondary text-[10px] uppercase tracking-wider">
                  {/* DATA — split into two columns */}
                  <th className="sticky top-8 z-20 px-2 py-1.5 bg-surface-raised">Dzień</th>
                  <th className="sticky top-8 z-20 px-2 py-1.5 bg-surface-raised">Data</th>

                  {/* SPRZEDAZ */}
                  <th className="sticky top-8 z-20 px-2 py-1.5 text-right bg-surface-raised">Zamów.</th>
                  <th className="sticky top-8 z-20 px-2 py-1.5 text-right bg-surface-raised">
                    Produkty <span className="text-[8px] opacity-60">(brutto)</span>
                  </th>
                  <th className="sticky top-8 z-20 px-2 py-1.5 text-right bg-surface-raised">
                    Przesyłka <span className="text-[8px] opacity-60">(brutto)</span>
                  </th>
                  <th className="sticky top-8 z-20 px-2 py-1.5 text-right bg-surface-raised">
                    Zwroty <span className="text-[8px] opacity-60">(brutto)</span>
                  </th>
                  <th className="sticky top-8 z-20 px-2 py-1.5 text-right bg-surface-raised">
                    Razem <span className="text-[8px] opacity-60">(brutto)</span>
                  </th>
                  {vatEnabled && <th className="sticky top-8 z-20 px-2 py-1.5 text-right bg-surface-raised">VAT</th>}

                  {/* KOSZTY */}
                  <th className="sticky top-8 z-20 px-2 py-1.5 text-right bg-surface-raised">
                    Produkty <span className="text-[8px] opacity-60">({vatEnabled ? 'netto' : 'brutto'})</span>
                  </th>
                  <th className="sticky top-8 z-20 px-2 py-1.5 text-right bg-surface-raised">
                    Wysyłka <span className="text-[8px] opacity-60">({vatEnabled ? 'netto' : 'brutto'})</span>
                  </th>
                  <th className="sticky top-8 z-20 px-2 py-1.5 text-right bg-surface-raised">
                    Prowizje <span className="text-[8px] opacity-60">(brutto)</span>
                  </th>
                  <th className="sticky top-8 z-20 px-2 py-1.5 text-right bg-surface-raised">
                    Stałe <span className="text-[8px] opacity-60">({vatEnabled ? 'netto' : 'brutto'})</span>
                  </th>

                  {/* REKLAMA */}
                  <th className="sticky top-8 z-20 px-2 py-1.5 text-right bg-surface-raised">Wydatki</th>
                  <th className="sticky top-8 z-20 px-2 py-1.5 text-right bg-surface-raised">CTR</th>
                  <th className="sticky top-8 z-20 px-2 py-1.5 text-right bg-surface-raised">CPC</th>
                  <th className="sticky top-8 z-20 px-2 py-1.5 text-right bg-surface-raised">ROAS</th>

                  {/* WYNIK */}
                  <th className="sticky top-8 z-20 px-2 py-1.5 text-right bg-surface-raised">Dochód</th>
                  <th className="sticky top-8 z-20 px-2 py-1.5 text-right bg-surface-raised">Zysk netto</th>
                </tr>
              </thead>

              <tbody>
                {daily.map((row, idx) => {
                  const isExpanded = expandedDate === row.date;
                  const channelEntry = channelDays[row.date];
                  return (
                  <Fragment key={row.date}>
                  <tr
                    onClick={() => toggleChannelDay(row.date)}
                    className={`border-b border-border-subtle hover:bg-surface-hover/30 transition-colors cursor-pointer ${
                      idx % 2 === 0 ? 'bg-surface-base/30' : ''
                    }`}
                  >
                    {/* DATA — split: day name + date */}
                    <td className="px-2 py-1.5 text-text-secondary">
                      <span className="inline-flex items-center gap-1">
                        {isExpanded ? (
                          <ChevronDown className="w-3 h-3 text-accent-primary" />
                        ) : (
                          <ChevronRight className="w-3 h-3 text-text-muted" />
                        )}
                        {formatDayName(row.date)}
                      </span>
                    </td>
                    <td className="px-2 py-1.5 text-text-secondary font-medium">
                      {formatDateOnly(row.date)}
                      {row._frozen && (
                        <span className="ml-1 text-accent-info" title="Dane zamrożone">&#10052;</span>
                      )}
                    </td>

                    {/* SPRZEDAZ */}
                    <td className="px-2 py-1.5 text-right text-text-secondary">{row.ordersCount}</td>
                    <td className="px-2 py-1.5 text-right text-success">{formatPLN(row.revenueGrossPreRefund ?? row.revenueGross)}</td>
                    <td className="px-2 py-1.5 text-right text-success">{formatPLN(row.shippingGrossPreRefund ?? row.shippingGross ?? 0)}</td>
                    <td className={`px-2 py-1.5 text-right ${(row.refundsGross ?? 0) > 0 ? 'text-danger' : 'text-text-muted'}`}>
                      {(row.refundsGross ?? 0) > 0 ? `-${formatPLN(row.refundsGross ?? 0)}` : formatPLN(0)}
                    </td>
                    <td className="px-2 py-1.5 text-right text-success font-medium">{formatPLN((row.revenueGross ?? 0) + (row.shippingGross ?? 0))}</td>
                    {vatEnabled && <td className="px-2 py-1.5 text-right text-text-secondary">{formatPLN((row.vatSales ?? 0) + (row.shippingVat ?? 0))}</td>}

                    {/* KOSZTY */}
                    <td className="px-2 py-1.5 text-right text-danger">{formatPLN(row.costProducts)}</td>
                    <td className="px-2 py-1.5 text-right text-danger">
                      {formatPLN(row.costShipping)}
                      {(row.costReturns ?? 0) > 0 && (
                        <div className="text-[10px] text-text-muted" title="Koszt zwrotu nieodebranych paczek">
                          zwrot: {formatPLN(row.costReturns ?? 0)}
                        </div>
                      )}
                    </td>
                    <td className="px-2 py-1.5 text-right text-danger">{formatPLN(row.costFees)}</td>
                    <td className="px-2 py-1.5 text-right text-danger">{formatPLN(row.costFixed)}</td>

                    {/* REKLAMA */}
                    <td className="px-2 py-1.5 text-right text-purple-400">{formatPLN(row.adSpend)}</td>
                    <td className="px-2 py-1.5 text-right text-purple-300">{row.ctr.toFixed(2)}%</td>
                    <td className="px-2 py-1.5 text-right text-purple-300">{formatPLN(row.cpc)}</td>
                    <td className="px-2 py-1.5 text-right text-purple-300">{row.roas.toFixed(2)}</td>

                    {/* WYNIK */}
                    <td className={`px-2 py-1.5 text-right font-medium ${row.grossProfit >= 0 ? 'text-success' : 'text-danger'}`}>
                      {formatPLN(row.grossProfit)}
                    </td>
                    <td className={`px-2 py-1.5 text-right font-medium ${row.netProfit >= 0 ? 'text-success' : 'text-danger'}`}>
                      {formatPLN(row.netProfit)}
                    </td>
                  </tr>

                  {/* ---- Channel breakdown: same columns, rows aligned with the main table ---- */}
                  {isExpanded && (!channelEntry || channelEntry.status === 'loading') && (
                    <tr className="border-b border-border-subtle bg-surface-base/60">
                      <td colSpan={tableColumnCount} className="px-4 py-3 text-xs text-text-muted">Liczenie rozbicia na kanały...</td>
                    </tr>
                  )}
                  {isExpanded && channelEntry?.status === 'error' && (
                    <tr className="border-b border-border-subtle bg-surface-base/60">
                      <td colSpan={tableColumnCount} className="px-4 py-3 text-xs text-warning">
                        Nie udało się pobrać rozbicia na kanały.{' '}
                        <button className="underline" onClick={(e) => { e.stopPropagation(); void loadChannelDay(row.date); }}>Spróbuj ponownie</button>
                      </td>
                    </tr>
                  )}
                  {isExpanded && channelEntry?.status === 'ok' && selectedProductIds.length > 0 && (
                    <tr className="border-b border-border-subtle bg-surface-base/60">
                      <td colSpan={tableColumnCount} className="px-4 py-3 text-xs text-text-muted">Rozbicie na kanały jest niedostępne przy filtrze produktów.</td>
                    </tr>
                  )}
                  {isExpanded && channelEntry?.status === 'ok' && selectedProductIds.length === 0 && channelEntry.rows.filter((r) => r.day).length === 0 && (
                    <tr className="border-b border-border-subtle bg-surface-base/60">
                      <td colSpan={tableColumnCount} className="px-4 py-3 text-xs text-text-muted">Brak rozbicia dla tego dnia.</td>
                    </tr>
                  )}
                  {isExpanded && channelEntry?.status === 'ok' && selectedProductIds.length === 0 && channelEntry.rows.filter((r) => r.day).map(({ channel, day: chDay }) => (
                    <tr key={`${row.date}-${channel}`} className="border-b border-border-subtle/60 bg-accent-primary/5">
                      {/* KANAŁ — scalone kolumny Dzień+Data (dzień wynika z wiersza nadrzędnego) */}
                      <td colSpan={2} className="px-2 py-1.5 pl-6 text-accent-primary font-medium">
                        {financeChannelLabels[(channel as FinanceChannel)] ?? channel}
                      </td>

                      {/* SPRZEDAZ */}
                      <td className="px-2 py-1.5 text-right text-text-secondary">{chDay!.ordersCount}</td>
                      <td className="px-2 py-1.5 text-right text-success">{formatPLN(chDay!.revenueGrossPreRefund ?? chDay!.revenueGross)}</td>
                      <td className="px-2 py-1.5 text-right text-success">{formatPLN(chDay!.shippingGrossPreRefund ?? chDay!.shippingGross ?? 0)}</td>
                      <td className={`px-2 py-1.5 text-right ${(chDay!.refundsGross ?? 0) > 0 ? 'text-danger' : 'text-text-muted'}`}>
                        {(chDay!.refundsGross ?? 0) > 0 ? `-${formatPLN(chDay!.refundsGross ?? 0)}` : formatPLN(0)}
                      </td>
                      <td className="px-2 py-1.5 text-right text-success font-medium">{formatPLN((chDay!.revenueGross ?? 0) + (chDay!.shippingGross ?? 0))}</td>
                      {vatEnabled && <td className="px-2 py-1.5 text-right text-text-secondary">{formatPLN((chDay!.vatSales ?? 0) + (chDay!.shippingVat ?? 0))}</td>}

                      {/* KOSZTY */}
                      <td className="px-2 py-1.5 text-right text-danger">{formatPLN(chDay!.costProducts)}</td>
                      <td className="px-2 py-1.5 text-right text-danger">
                        {formatPLN(chDay!.costShipping)}
                        {(chDay!.costReturns ?? 0) > 0 && (
                          <div className="text-[10px] text-text-muted" title="Koszt zwrotu nieodebranych paczek">
                            zwrot: {formatPLN(chDay!.costReturns ?? 0)}
                          </div>
                        )}
                      </td>
                      <td className="px-2 py-1.5 text-right text-danger">{formatPLN(chDay!.costFees)}</td>
                      <td className="px-2 py-1.5 text-right text-danger">{formatPLN(chDay!.costFixed)}</td>

                      {/* REKLAMA */}
                      <td className="px-2 py-1.5 text-right text-purple-400">{formatPLN(chDay!.adSpend)}</td>
                      <td className="px-2 py-1.5 text-right text-purple-300">{chDay!.ctr.toFixed(2)}%</td>
                      <td className="px-2 py-1.5 text-right text-purple-300">{formatPLN(chDay!.cpc)}</td>
                      <td className="px-2 py-1.5 text-right text-purple-300">{chDay!.roas.toFixed(2)}</td>

                      {/* WYNIK */}
                      <td className={`px-2 py-1.5 text-right font-medium ${chDay!.grossProfit >= 0 ? 'text-success' : 'text-danger'}`}>
                        {formatPLN(chDay!.grossProfit)}
                      </td>
                      <td className={`px-2 py-1.5 text-right font-medium ${chDay!.netProfit >= 0 ? 'text-success' : 'text-danger'}`}>
                        {formatPLN(chDay!.netProfit)}
                      </td>
                    </tr>
                  ))}
                  {isExpanded && channelEntry?.status === 'ok' && selectedProductIds.length === 0 && channelEntry.rows.some((r) => r.day) && (
                    <tr className="border-b border-border-subtle">
                      <td colSpan={tableColumnCount} className="px-4 py-2 text-[11px] text-text-muted">
                        Koszty stałe rozłożone proporcjonalnie do sprzedaży, wydatki reklamowe w całości przypisane do kanału Meta Ads.
                        Kanał pochodzi z pomiaru wejść (Finanse → Ustawienia); zamówienia sprzed pomiaru lub bez zgody cookies lądują w „Nieustalone".
                      </td>
                    </tr>
                  )}
                  </Fragment>
                  );
                })}

                {/* ---- SUMA (totals) row ---- */}
                {totals && (
                  <tr className="bg-surface-hover/50 border-t-2 border-accent-primary/30 font-bold text-xs">
                    <td className="px-2 py-2 text-accent-primary uppercase tracking-wider" colSpan={2}>SUMA</td>

                    <td className="px-2 py-2 text-right text-text-primary">{totals.ordersCount}</td>
                    <td className="px-2 py-2 text-right text-success">{formatPLN(totals.revenueGrossPreRefund ?? totals.revenueGross)}</td>
                    <td className="px-2 py-2 text-right text-success">{formatPLN(totals.shippingGrossPreRefund ?? totals.shippingGross ?? 0)}</td>
                    <td className={`px-2 py-2 text-right ${(totals.refundsGross ?? 0) > 0 ? 'text-danger' : 'text-text-muted'}`}>
                      {(totals.refundsGross ?? 0) > 0 ? `-${formatPLN(totals.refundsGross ?? 0)}` : formatPLN(0)}
                    </td>
                    <td className="px-2 py-2 text-right text-success">{formatPLN((totals.revenueGross ?? 0) + (totals.shippingGross ?? 0))}</td>
                    {vatEnabled && <td className="px-2 py-2 text-right text-text-secondary">{formatPLN((totals.vatSales ?? 0) + (totals.shippingVat ?? 0))}</td>}

                    <td className="px-2 py-2 text-right text-danger">{formatPLN(totals.costProducts)}</td>
                    <td className="px-2 py-2 text-right text-danger">
                      {formatPLN(totals.costShipping)}
                      {(totals.costReturns ?? 0) > 0 && (
                        <div className="text-[10px] text-text-muted" title="Koszt zwrotu nieodebranych paczek">
                          zwrot: {formatPLN(totals.costReturns ?? 0)}
                        </div>
                      )}
                    </td>
                    <td className="px-2 py-2 text-right text-danger">{formatPLN(totals.costFees)}</td>
                    <td className="px-2 py-2 text-right text-danger">{formatPLN(totals.costFixed)}</td>

                    <td className="px-2 py-2 text-right text-purple-300">{formatPLN(totals.adSpend)}</td>
                    <td className="px-2 py-2 text-right text-purple-200">{totals.ctr.toFixed(2)}%</td>
                    <td className="px-2 py-2 text-right text-purple-200">{formatPLN(totals.cpc)}</td>
                    <td className="px-2 py-2 text-right text-purple-200">{totals.roas.toFixed(2)}</td>

                    <td className={`px-2 py-2 text-right ${totals.grossProfit >= 0 ? 'text-success' : 'text-danger'}`}>
                      {formatPLN(totals.grossProfit)}
                    </td>
                    <td className={`px-2 py-2 text-right ${totals.netProfit >= 0 ? 'text-success' : 'text-danger'}`}>
                      {formatPLN(totals.netProfit)}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {daily.length === 0 && !loading && (
            <EmptyState
              icon={CalendarDays}
              title="Brak danych dla wybranego zakresu dat"
              size="sm"
            />
          )}
        </div>
      )}

      {/* ============================================================= */}
      {/* Tax breakdown section                                         */}
      {/* ============================================================= */}
      {!loading && tax && (
        <div className="rounded-xl border border-border-subtle bg-surface-raised/50 p-6 space-y-4">
          <h2 className="text-lg font-semibold text-text-primary">Podsumowanie podatkowe</h2>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {/* VAT do zaplaty — always shown */}
            <div className="flex items-center justify-between p-3 rounded-lg bg-surface-base/50 border border-border-subtle">
              <span className="text-text-secondary text-sm">VAT do zapłaty</span>
              <span className={`font-semibold ${tax.vatToPay > 0 ? 'text-text-primary' : 'text-text-muted'}`}>{formatPLN(tax.vatToPay)} zł</span>
            </div>

            {/* Podatek dochodowy — only if enabled */}
            {taxSettings.includeZusTaxes && taxSettings.incomeTax && (
              <div className="flex items-center justify-between p-3 rounded-lg bg-surface-base/50 border border-border-subtle">
                <span className="text-text-secondary text-sm">Podatek dochodowy</span>
                <span className="text-text-primary font-semibold">{formatPLN(tax.incomeTax)} zł</span>
              </div>
            )}

            {/* Skladka zdrowotna — only if enabled */}
            {taxSettings.includeZusTaxes && taxSettings.healthIns && (
              <div className="flex items-center justify-between p-3 rounded-lg bg-surface-base/50 border border-border-subtle">
                <span className="text-text-secondary text-sm">Składka zdrowotna</span>
                <span className="text-text-primary font-semibold">{formatPLN(tax.healthIns)} zł</span>
              </div>
            )}

            {/* ZUS spoleczny — only if enabled */}
            {taxSettings.includeZusTaxes && taxSettings.socialIns && (
              <div className="flex items-center justify-between p-3 rounded-lg bg-surface-base/50 border border-border-subtle">
                <span className="text-text-secondary text-sm">ZUS społeczny</span>
                <span className="text-text-primary font-semibold">{formatPLN(tax.socialIns)} zł</span>
              </div>
            )}
          </div>

          {/* RAZEM — only when at least one tax is enabled */}
          {taxSettings.includeZusTaxes && (taxSettings.incomeTax || taxSettings.healthIns || taxSettings.socialIns) && (
            <div className="flex items-center justify-between p-4 rounded-lg bg-accent-primary/5 border border-accent-primary/20">
              <span className="text-accent-primary font-semibold">RAZEM podatki i składki</span>
              <span className="text-accent-primary font-bold text-lg">{formatPLN(tax.totalTaxes)} zł</span>
            </div>
          )}

          {/* FB Ads WNT info box */}
          {tax.adVatWnt > 0 && (
            <div className="p-4 rounded-lg bg-purple-500/10 border border-purple-500/20">
              <p className="text-sm text-purple-300">
                <span className="font-semibold">FB Ads WNT:</span>{' '}
                Wewnątrzwspólnotowe nabycie usług reklamowych (Facebook/Meta) &mdash;{' '}
                <span className="text-text-primary font-medium">{formatPLN(tax.adVatWnt)} zł</span>.
                Kwota ta powinna zostać wykazana w deklaracji VAT jako import usług (WNT).
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
